import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Cpu, Sparkles, TrendingUp, Zap, ArrowRight, ArrowDown,
  Mail, Send, ExternalLink, Menu, X, Settings, Search, Wallet, Building2, FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-studio-hub-33/artifacts/l67b1y1e_Color%20logo%20-%20no%20background.png";
const WHITE_LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-studio-hub-33/artifacts/2ynjcy6l_White%20logo%20-%20no%20background.png";

const AI_TYPE_CONFIG = {
  agentic: { label: "AGENTIC AI", class: "badge-agentic", icon: Cpu },
  generative: { label: "GENERATIVE AI", class: "badge-generative", icon: Sparkles },
  predictive: { label: "PREDICTIVE AI", class: "badge-predictive", icon: TrendingUp },
  nextgen: { label: "NEXT-GEN AI", class: "badge-nextgen", icon: Zap },
};

const STATUS_CONFIG = {
  live: { label: "LIVE", class: "status-live" },
  beta: { label: "BETA", class: "status-beta" },
  coming_soon: { label: "COMING SOON", class: "status-coming_soon" },
};

// Product logo lockups — marks recreated 1:1 from each product's own site header,
// paired with their wordmark, linked to the live product
// Brand config for each launched venture — drives card colours, mark, wordmark
const VENTURE_BRANDS = {
  V1: {
    // Kaya — solid orange brand color as background
    primary: '#D94E18',
    bg: 'linear-gradient(145deg, #E05420 0%, #B83C10 100%)',
    border: '#D94E18',
    boxShadow: '0 0 40px rgba(217,78,24,0.35)',
    borderRadius: '1rem',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.92)',
    liveBg: 'rgba(255,255,255,0.2)',
    liveText: '#ffffff',
    font: "'Manrope', sans-serif",
    mark: (
      <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ border: '1.5px solid rgba(255,255,255,0.5)' }}>
        <Building2 className="text-white" size={26} strokeWidth={1.5} />
      </div>
    ),
    wordmark: (
      <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#ffffff', fontSize: '1.6rem', letterSpacing: '-0.02em' }}>
        kaya.ai
      </span>
    ),
  },
  V2: {
    // Decode — solid blue brand color as background
    primary: '#2563EB',
    bg: 'linear-gradient(145deg, #3070FF 0%, #1A4FCC 100%)',
    border: '#2563EB',
    boxShadow: '0 0 40px rgba(37,99,235,0.35)',
    borderRadius: '1rem',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.92)',
    liveBg: 'rgba(255,255,255,0.2)',
    liveText: '#ffffff',
    font: "'Outfit', sans-serif",
    mark: (
      <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0" style={{ border: '1.5px solid rgba(255,255,255,0.5)' }}>
        <Zap className="text-white" size={22} strokeWidth={2} />
      </div>
    ),
    wordmark: (
      <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: '#ffffff', fontSize: '1.6rem', letterSpacing: '-0.01em' }}>
        Decoded
      </span>
    ),
  },
  V3: {
    // Nia — solid teal brand color as background
    primary: '#14b8a6',
    bg: 'linear-gradient(145deg, #1ACFBC 0%, #0D9488 100%)',
    border: '#14b8a6',
    boxShadow: '0 0 40px rgba(20,184,166,0.35)',
    borderRadius: '0.75rem',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.92)',
    liveBg: 'rgba(255,255,255,0.2)',
    liveText: '#ffffff',
    font: "'Manrope', sans-serif",
    mark: (
      <div className="relative h-12 w-12 flex-shrink-0" style={{ border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: '0.75rem' }}>
        <div className="absolute inset-0 rounded-xl bg-white/20">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/10 to-transparent" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white font-black text-xl tracking-tight">n</span>
        </div>
        <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-300 border-2 border-[#14b8a6]" />
      </div>
    ),
    wordmark: (
      <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 900, color: '#ffffff', fontSize: '1.6rem', letterSpacing: '-0.02em' }}>
        nia ai
      </span>
    ),
  },
};

const LaunchedVentureCard = ({ venture }) => {
  const brand = VENTURE_BRANDS[venture.id];
  if (!brand) return null;

  return (
    <div
      className="p-7 flex flex-col h-full"
      style={{ background: brand.bg, border: `2px solid ${brand.border}`, borderRadius: brand.borderRadius || '0', boxShadow: brand.boxShadow || 'none' }}
      data-testid={`launched-${venture.id}`}
    >
      {/* Mark + LIVE badge */}
      <div className="flex items-start justify-between mb-6">
        <a
          href={venture.link}
          target="_blank"
          rel="noopener noreferrer"
          className="transition-opacity hover:opacity-75"
          data-testid={`venture-icon-link-${venture.id}`}
          style={{ cursor: 'pointer' }}
        >
          {brand.mark}
        </a>
        <span
          className="text-xs uppercase px-3 py-1"
          style={{ fontFamily: brand.font || "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: brand.liveText, background: brand.liveBg, border: `1px solid ${brand.primary}50`, borderRadius: brand.borderRadius ? '999px' : '0' }}
        >
          LIVE
        </span>
      </div>

      {/* Wordmark */}
      <div className="mb-2">{brand.wordmark}</div>

      {/* Category */}
      {venture.category && (
        <p className="text-xs uppercase mb-5" style={{ fontFamily: brand.font || "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.12em' }}>
          {venture.category}
        </p>
      )}

      {/* Description */}
      {venture.description && (
        <p className="text-sm flex-grow mb-4" style={{ fontWeight: 400, lineHeight: 1.65, color: brand.textSecondary, fontFamily: brand.font || 'inherit' }}>
          {venture.description}
        </p>
      )}

      {/* Divider + Visit */}
      {venture.link && (
        <div className="border-t border-white/20 pt-4 mt-auto">
          <a
            href={venture.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full transition-all hover:bg-white/30"
            style={{ fontFamily: brand.font || "'Plus Jakarta Sans', sans-serif", color: '#ffffff', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)' }}
            data-testid={`venture-logo-link-${venture.id}`}
          >
            VISIT <ExternalLink size={12} />
          </a>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#150900]/95 backdrop-blur-sm border-b border-[#333]">
      <div className="container-main">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3" data-testid="logo-link" style={{ cursor: 'pointer' }}>
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: '1.35rem',
                color: '#F07020',
                letterSpacing: '-0.03em',
              }}
            >
              inopsia
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            <button 
              onClick={() => scrollToSection("ventures")} 
              className="nav-link font-bold"
              data-testid="nav-ventures"
            >
              Ventures
            </button>
            <button 
              onClick={() => scrollToSection("roadmap")} 
              className="nav-link font-bold"
              data-testid="nav-roadmap"
            >
              Roadmap
            </button>
            <button 
              onClick={() => scrollToSection("about")} 
              className="nav-link font-bold"
              data-testid="nav-about"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection("contact")} 
              className="nav-link font-bold"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </nav>

          <div className="hidden md:block">
            <button 
              onClick={() => scrollToSection("contact")}
              className="btn-primary"
              data-testid="header-cta"
            >
              Get in Touch
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#150900] border-t border-[#222]">
          <div className="container-main py-6 flex flex-col gap-4">
            <button onClick={() => scrollToSection("ventures")} className="nav-link text-left py-2">Ventures</button>
            <button onClick={() => scrollToSection("roadmap")} className="nav-link text-left py-2">Roadmap</button>
            <button onClick={() => scrollToSection("about")} className="nav-link text-left py-2">About</button>
            <button onClick={() => scrollToSection("contact")} className="nav-link text-left py-2">Contact</button>
            <button onClick={() => scrollToSection("contact")} className="btn-primary mt-4">Get in Touch</button>
          </div>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  return (
    <section className="hero-section flex items-center justify-center pt-24 overflow-x-hidden" data-testid="hero-section">
      <div className="hero-glow" />
      <div className="container-main relative z-10 text-center py-20 md:py-28">
        <div className="animate-fade-in-up">
          <p className="text-xs md:text-sm uppercase tracking-[0.28em] text-[#F07020] mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
            Business &amp; Finance AI Studio
          </p>
        </div>
        
        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-8 animate-fade-in-up delay-100" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
          Where business<br />
          operators build<br />
          <span className="text-[#F07020]">AI ventures.</span>
        </h1>
        
        <p className="text-lg md:text-xl text-gray-300 max-w-xl mx-auto animate-fade-in-up delay-200" style={{ fontWeight: 400, lineHeight: 1.6 }}>
          We build AI companies that solve real business problems — because we've faced them ourselves.
        </p>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 border-t border-[#333]" data-testid="about-section">
      <div className="container-main">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#F07020] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            About Us
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: "-0.02em" }}>
            Built from<br />
            <span className="text-[#F07020]">experience</span>
          </h2>
          <p className="text-gray-300 text-xl md:text-2xl leading-relaxed mb-6" style={{ fontWeight: 400 }}>
            Every venture we build starts with a problem we've faced ourselves — 
            in finance, in operations, in building businesses.
          </p>
          <p className="text-gray-400 text-lg md:text-xl leading-relaxed" style={{ fontWeight: 400 }}>
            Our hope is simple: if we've struggled with something, others probably have too. 
            So we build solutions that help — and we make them AI-native from day one.
          </p>
        </div>
      </div>
    </section>
  );
};

const VentureCard = ({ product }) => {
  const aiConfig = AI_TYPE_CONFIG[product.ai_type] || AI_TYPE_CONFIG.agentic;
  const statusConfig = STATUS_CONFIG[product.status] || STATUS_CONFIG.coming_soon;
  const IconComponent = aiConfig.icon;

  return (
    <div className="card-base product-card p-8 h-full flex flex-col border-2 border-[#333]" data-testid={`venture-card-${product.id}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="p-4 bg-[#0D0500] border-2 border-[#444]">
          <IconComponent className="text-[#F07020]" size={32} strokeWidth={2.5} />
        </div>
        <span className={`text-xs px-3 py-1 ${statusConfig.class}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
          {statusConfig.label}
        </span>
      </div>
      
      <span className={`text-xs px-3 py-1 inline-block w-fit mb-4 ${aiConfig.class}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
        {aiConfig.label}
      </span>
      
      <h3 className="text-2xl md:text-3xl uppercase mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
        {product.name}
      </h3>
      
      <p className="text-gray-300 text-base mb-6 flex-grow" style={{ fontWeight: 400 }}>
        {product.description}
      </p>
      
      {product.features && product.features.length > 0 && (
        <ul className="mb-6 space-y-3">
          {product.features.slice(0, 3).map((feature, idx) => (
            <li key={idx} className="flex items-center gap-3 text-base text-gray-400" style={{ fontWeight: 400 }}>
              <span className="w-2 h-2 bg-[#F07020]" />
              {feature}
            </li>
          ))}
        </ul>
      )}
      
      {product.demo_link && product.status === 'live' && (
        <a 
          href={product.demo_link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn-outline w-full flex items-center justify-center gap-2 mt-auto"
          data-testid={`venture-link-${product.id}`}
        >
          VISIT <ExternalLink size={18} strokeWidth={2.5} />
        </a>
      )}
    </div>
  );
};

const VenturesSection = ({ products }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [ventures, setVentures] = useState([]);
  const [loadingVentures, setLoadingVentures] = useState(true);

  // Fetch ventures from API
  useEffect(() => {
    const fetchVentures = async () => {
      try {
        const response = await axios.get(`${API}/ventures`);
        // Convert launch_date strings to Date objects
        const venturesWithDates = response.data.map(v => ({
          ...v,
          launchDate: new Date(v.launch_date)
        }));
        setVentures(venturesWithDates);
      } catch (error) {
        console.error("Failed to fetch ventures:", error);
        // Fallback to hardcoded if API fails
        setVentures([
          { id: "V1", quarter: "Q1", year: "2026", launchDate: new Date("2026-03-31T23:59:59"), category: "BOOKKEEPING", name: "Kaya", description: "Your AI accountant that learns your business", link: "https://kayaworld.ai" },
          { id: "V2", quarter: "Q2", year: "2026", launchDate: new Date("2026-06-30T23:59:59"), category: "TRADE TECH", name: "Decode", description: "Turn Tariff Complexity into Competitive Advantage", link: "https://decodedworld.ai" },
          { id: "V3", quarter: "Q2", year: "2026", launchDate: new Date("2026-06-30T23:59:59"), category: "OPS TECH", name: "Nia", description: "The operating system for AI and software companies.", link: "https://niaworld.ai" },
          { id: "V4", quarter: "Q4", year: "2026", launchDate: new Date("2026-12-31T23:59:59"), category: "FORENSIC TECH", name: null, description: null, link: null },


        ]);
      } finally {
        setLoadingVentures(false);
      }
    };
    fetchVentures();
  }, []);

  // Separate launched vs upcoming ventures
  const launchedVentures = ventures.filter(v => currentDate > v.launchDate);
  const upcomingVentures = ventures.filter(v => currentDate <= v.launchDate);
  
  // Get next quarter to launch (earliest upcoming)
  const nextLaunchDate = upcomingVentures.length > 0 
    ? upcomingVentures.reduce((min, v) => v.launchDate < min ? v.launchDate : min, upcomingVentures[0].launchDate)
    : null;
  
  // Ventures launching next (same quarter as nearest launch)
  const launchingNext = upcomingVentures.filter(v => 
    nextLaunchDate && v.launchDate.getTime() === nextLaunchDate.getTime()
  );
  
  // Future ventures (after next launch)
  const futureVentures = upcomingVentures.filter(v => 
    nextLaunchDate && v.launchDate.getTime() > nextLaunchDate.getTime()
  );

  // Update current date every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Countdown to next launch
  useEffect(() => {
    if (!nextLaunchDate) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = nextLaunchDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextLaunchDate]);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/notify`, { email });
      toast.success("You're on the list! We'll notify you when we launch.");
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Get quarter label for next launch
  const getNextQuarterLabel = () => {
    if (launchingNext.length === 0) return "";
    return `${launchingNext[0].quarter} ${launchingNext[0].year}`;
  };

  return (
    <section id="ventures" className="py-24 md:py-32 border-t border-[#333]" data-testid="ventures-section">
      <div className="container-main">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.2em] text-[#F07020] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Our Ventures
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: "-0.02em" }}>
            AI companies that <span className="text-[#F07020]">deliver</span>
          </h2>
        </div>

        {/* LAUNCHED VENTURES - Only shows if any have launched */}
        {launchedVentures.length > 0 && (
          <div className="mb-20">
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-500 mb-6 text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Launched
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {launchedVentures.map((venture) => (
                <LaunchedVentureCard key={venture.id} venture={venture} />
              ))}
            </div>
          </div>
        )}

        {/* COMING NEXT - V4 Spotlight */}
        {launchingNext.length > 0 && (
          <div id="roadmap" className="text-center">
            <div className="mb-16">

              {launchingNext.map((venture) => {
                const getVentureIcon = () => {
                  switch(venture.category) {
                    case "FORENSIC TECH": return <Search size={28} />;
                    case "TRADE TECH": return <TrendingUp size={28} />;
                    case "OPS TECH": return <Settings size={28} />;
                    case "FINTECH": return <Wallet size={28} />;
                    default: return <Zap size={28} />;
                  }
                };

                return (
                  <div
                    key={venture.id}
                    className="max-w-2xl mx-auto text-left rounded-2xl overflow-hidden mb-8"
                    style={{
                      background: '#0C0E12',
                      border: '1px solid rgba(255,255,255,0.09)',
                      borderTop: '3px solid #F07020',
                      boxShadow: '0 32px 72px rgba(0,0,0,0.55)'
                    }}
                    data-testid="v4-spotlight-card"
                  >
                    <div className="p-8 md:p-10">
                      {/* Top label row */}
                      <div className="flex items-center gap-3 mb-7">
                        <span
                          className="text-xs uppercase tracking-[0.18em] px-3 py-1 rounded-full"
                          style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontWeight: 700,
                            color: '#F07020',
                            background: 'rgba(240,112,32,0.1)',
                            border: '1px solid rgba(240,112,32,0.3)'
                          }}
                        >
                          Coming Next
                        </span>
                        <span className="text-xs uppercase tracking-[0.18em]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                          · {venture.quarter} {venture.year}
                        </span>
                      </div>

                      {/* Category */}
                      {venture.category && (
                        <p className="text-xs uppercase tracking-[0.22em] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
                          {venture.category}
                        </p>
                      )}

                      {/* Headline */}
                      {venture.description && (
                        <h3 className="text-3xl md:text-4xl text-white mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                          {venture.description}
                        </h3>
                      )}

                      {/* Divider */}
                      <div className="w-10 h-[2px] mb-6" style={{ background: 'rgba(255,255,255,0.25)' }} />

                      {/* Long description */}
                      {venture.long_description && venture.long_description.split('\n\n').map((para, i) => (
                        <p key={i} className="mb-4 last:mb-0" style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontWeight: i === 0 ? 600 : 400,
                          fontSize: i === 0 ? '1.05rem' : '0.9375rem',
                          color: i === 0 ? '#ffffff' : 'rgba(255,255,255,0.72)',
                          lineHeight: 1.75
                        }}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              <p className="text-xl md:text-2xl text-white mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                LAUNCHING <span className="text-[#F07020]">{getNextQuarterLabel()}</span>
              </p>
              
              {/* Countdown */}
              <div className="flex justify-center gap-4 md:gap-6 mb-10">
                <div className="text-center">
                  <div className="bg-[#0D0500] border border-[#333] px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px] rounded-xl">
                    <p className="text-2xl md:text-4xl text-[#F07020]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>{timeLeft.days}</p>
                  </div>
                  <p className="text-xs uppercase text-gray-500 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>Days</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#0D0500] border border-[#333] px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px] rounded-xl">
                    <p className="text-2xl md:text-4xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>{String(timeLeft.hours).padStart(2, '0')}</p>
                  </div>
                  <p className="text-xs uppercase text-gray-500 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>Hours</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#0D0500] border border-[#333] px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px] rounded-xl">
                    <p className="text-2xl md:text-4xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>{String(timeLeft.minutes).padStart(2, '0')}</p>
                  </div>
                  <p className="text-xs uppercase text-gray-500 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>Minutes</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#0D0500] border border-[#333] px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px] rounded-xl">
                    <p className="text-2xl md:text-4xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>{String(timeLeft.seconds).padStart(2, '0')}</p>
                  </div>
                  <p className="text-xs uppercase text-gray-500 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>Seconds</p>
                </div>
              </div>
              
              <p className="text-gray-400 mb-6" style={{ fontWeight: 400 }}>
                Be the first to know when we go live.
              </p>
              
              {/* Email signup */}
              <form onSubmit={handleNotify} className="max-w-md mx-auto flex flex-col sm:flex-row gap-4 mb-12" data-testid="notify-form">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base flex-1 text-base py-4 border-2"
                  placeholder="Enter your email"
                  data-testid="notify-email"
                />
                <button 
                  type="submit" 
                  disabled={submitting}
                  className="btn-primary whitespace-nowrap"
                  data-testid="notify-submit"
                >
                  {submitting ? "..." : "GET NOTIFIED"}
                </button>
              </form>
              
              {/* Arrow pointing to roadmap - only if there are future ventures */}
              {futureVentures.length > 0 && (
                <a 
                  href="#roadmap" 
                  className="inline-flex flex-col items-center gap-2 text-gray-500 hover:text-[#F07020] transition-colors mb-8"
                  data-testid="scroll-to-roadmap"
                >
                  <span className="text-sm uppercase tracking-wider" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    View Full Roadmap
                  </span>
                  <ArrowDown size={24} className="animate-bounce" />
                </a>
              )}
            </div>

            {/* ROADMAP - Only shows future ventures */}
            {futureVentures.length > 0 && (
              <div id="roadmap" className="border-t border-[#333] pt-16">
                <p className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-12 text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                  2026 Roadmap
                </p>
                
                {/* Horizontal Timeline */}
                <div className="relative max-w-5xl mx-auto">
                  {/* Timeline Line */}
                  <div className="hidden md:block absolute top-8 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#333] to-transparent" />
                  
                  <div className="flex flex-col md:flex-row md:justify-between gap-8 md:gap-4">
                    {futureVentures.map((venture, index) => {
                      // Get icon based on category
                      const getIcon = () => {
                        switch(venture.category) {
                          case "BOOKKEEPING": return <FileSpreadsheet size={20} />;
                          case "TRADE TECH": return <TrendingUp size={20} />;
                          case "OPS TECH": return <Settings size={20} />;
                          case "FORENSIC TECH": return <Search size={20} />;
                          case "FINTECH": return <Wallet size={20} />;
                          case "ENTERPRISE TECH": return <Building2 size={20} />;
                          default: return <Zap size={20} />;
                        }
                      };
                      
                      return (
                        <div 
                          key={venture.id}
                          className="flex-1 relative group"
                          data-testid={`roadmap-${venture.id}`}
                        >
                          {/* Timeline Dot */}
                          <div className="hidden md:flex justify-center mb-4">
                            <div className="w-4 h-4 rounded-full bg-[#F07020]/20 border-2 border-[#F07020]/50 group-hover:bg-[#F07020]/40 group-hover:border-[#F07020] transition-all" />
                          </div>
                          
                          {/* Card */}
                          <div className="p-5 border border-[#F07020]/20 bg-[#F07020]/5 hover:border-[#F07020]/50 hover:bg-[#F07020]/10 transition-all text-center rounded-lg">
                            {/* Quarter badge */}
                            <span className="inline-block text-xs px-2 py-0.5 rounded-full mb-4 border border-[#F07020]/30 text-[#F07020]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                              {venture.quarter} {venture.year}
                            </span>
                            
                            {/* Icon */}
                            <div className="flex justify-center mb-4">
                              <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-[#F07020]/30 bg-[#F07020]/10 text-[#F07020] group-hover:bg-[#F07020]/20 transition-all">
                                {getIcon()}
                              </div>
                            </div>
                            
                            {venture.category && (
                              <p className="text-white text-sm font-bold mb-2 group-hover:text-[#F07020] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                                {venture.category}
                              </p>
                            )}
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
                              In Stealth
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ALL LAUNCHED - When everything is done */}
        {launchingNext.length === 0 && launchedVentures.length > 0 && (
          <div className="text-center py-12">
            <p className="text-2xl text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              ALL VENTURES <span className="text-emerald-500">LAUNCHED</span>
            </p>
            <p className="text-gray-400" style={{ fontWeight: 400 }}>
              Our 2026 roadmap is complete. More ventures coming soon.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll be in touch.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="contact-section py-24 md:py-32 border-t border-[#333]" data-testid="contact-section">
      <div className="container-main">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-[#F07020] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Get in Touch
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: "-0.02em" }}>
              Let's build <span className="text-[#F07020]">AI</span> together
            </h2>
            <p className="text-gray-300 text-lg" style={{ fontWeight: 400 }}>
              Have a project in mind? Want to collaborate? Drop us a line.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-base w-full text-base py-4 border-2"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="form-label">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-base w-full text-base py-4 border-2"
                  placeholder="your@email.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            
            <div>
              <label className="form-label">Message</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="input-base w-full min-h-[150px] text-base border-2"
                placeholder="Tell us about your project..."
                data-testid="contact-message"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-3 text-base"
              data-testid="contact-submit"
            >
              {loading ? "SENDING..." : "SEND MESSAGE"} <Send size={20} strokeWidth={2.5} />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="footer py-12 border-t-2 border-[#333]" data-testid="footer">
      <div className="container-main">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: '1.1rem', color: '#F07020', letterSpacing: '-0.03em' }}>
              inopsia
            </span>
            <span className="text-gray-500 text-sm" style={{ fontWeight: 400 }}>
              Business &amp; Finance AI Studio
            </span>
          </div>
          
          <div className="flex items-center gap-8">
            <a href="/admin" className="text-gray-500 hover:text-white text-sm uppercase" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }} data-testid="footer-admin-link">
              Admin
            </a>
            <a href="mailto:hello@inopsia.com" className="text-gray-400 hover:text-[#F07020] text-base flex items-center gap-2" style={{ fontWeight: 400 }}>
              <Mail size={18} strokeWidth={2.5} /> hello@inopsia.com
            </a>
          </div>
        </div>
        
        <div className="border-t-2 border-[#333] mt-8 pt-8 text-center">
          <p className="text-gray-500 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
            &copy; {new Date().getFullYear()} Inopsia. Where business operators build AI ventures.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <main>
      <Header />
      <HeroSection />
      <AboutSection />
      <VenturesSection products={products} />
      <ContactSection />
      <Footer />
    </main>
  );
}
